import { Button, Menu, MenuItem } from "@mui/material";
import {
  DataGridPremium,
  GRID_REORDER_COL_DEF,
  GridCellEditStartReasons,
  GridCellModes,
  GridCellModesModel,
  GridCellParams,
  GridColDef,
  GridRowClassNameParams,
  GridRowModel,
  GridRowOrderChangeParams,
  GridRowSelectionModel,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import json2md from "json2md";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import ExpandInput from "src/components/Form/Elements/Table/ExpandInput";
import {
  autosizeOptions,
  parseTable,
  Props,
  slots,
  tableStyles,
} from "src/components/Form/Elements/Table/helpers";
import { ShineBackground } from "src/components/ShineBorder/ShineBackground";

const LoadingOverlay = () => {
  return (
    <div className="h-1 w-full relative" data-testid="loading-overlay">
      <ShineBackground />
    </div>
  );
};

export const Table = forwardRef(
  ({ element, value, isFetchingSuggestion, onChange }: Props, ref) => {
    const apiRef = useGridApiRef();

    const readOnly = onChange === undefined;

    const copyTable = () => {
      const rows = apiRef.current.getRowModels();
      const headers = columns
        .filter((col) => col.field !== "__reorder__")
        .map((col) => col.headerName);

      const escapeCell = (cell: string) => {
        if (cell == null) return "";
        // Replace all \n with \r\n for Excel compatibility
        let value = String(cell).replace(/\n/g, "\r\n");
        // If cell contains tab, newline, or quote, wrap in quotes and escape quotes
        if (/[\"\t\r\n]/.test(value)) {
          value = '"' + value.replace(/"/g, '""') + '"';
        }
        return value;
      };

      const tableData = [
        headers.join("\t"),
        ...Array.from(rows.values()).map((row: GridRowModel) =>
          headers
            .filter((header) => header !== undefined)
            .map((header) => escapeCell(row[header] || ""))
            .join("\t")
        ),
      ].join("\n");

      navigator.clipboard
        .writeText(tableData)
        .then(() => {
          // Optionally show a success message
        })
        .catch((err) => {
          console.error("Failed to copy table: ", err);
        });
    };

    useImperativeHandle(ref, () => ({
      copyTable,
    }));

    const columns: GridColDef[] = useMemo(
      () =>
        [
          {
            ...GRID_REORDER_COL_DEF,
            field: "__reorder__",
            maxWidth: 54, // padding + icon
          },
        ].concat(
          element.options.columns.map((col) => ({
            field: col.name,
            headerName: col.name,
            flex: col.options?.flex ?? 1,
            type: col.options?.type ?? "string",
            valueOptions: col.options?.valueOptions,
            editable: readOnly ? false : (col.options?.editable ?? true),
            // needs to be set but I forgot why. Sorry.
            maxWidth: col.options?.maxWidth ?? 10000,
            // make multiline by default
            ...((col.options?.multiline ?? true)
              ? {
                  renderEditCell: (params) => (
                    <ExpandInput
                      {...params}
                      handleUpdateRowData={handleUpdateRowData}
                      rowData={rowData}
                    />
                  ),
                  renderCell: (params) => {
                    const lines = params.value.split("\n");
                    return (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        {lines.map((line: string, index: number) => (
                          <span key={index}>{line || <br />}</span>
                        ))}
                      </div>
                    );
                  },
                }
              : {}),
          }))
        ),
      [element.options.columns, readOnly]
    );

    let rowData: Record<string, string>[] = useMemo(() => {
      const parsedData = parseTable(element.options.columns, value);
      // Convert <br /> tags to newlines for display
      return parsedData.map((row) => {
        const processedRow = { ...row };
        Object.keys(processedRow).forEach((key) => {
          if (typeof processedRow[key] === "string") {
            processedRow[key] = processedRow[key].replace(/<br\s*\/?>/g, "\n");
          }
        });
        return processedRow;
      });
    }, [value]);

    // update the layout when the data changes
    useEffect(() => {
      apiRef.current.autosizeColumns(autosizeOptions);
    }, [rowData]);

    const [selectedRow, setSelectedRow] = useState<string>();

    const [contextMenu, setContextMenu] = useState<{
      mouseX: number;
      mouseY: number;
    } | null>(null);

    const handleContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();

      const id = event.currentTarget.getAttribute("data-id");

      if (!id) return;

      setSelectedRow(id);
      setContextMenu(
        contextMenu === null
          ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
          : null
      );
    };

    /**
     * Close the context menu
     */
    const handleClose = () => {
      setContextMenu(null);
    };

    /**
     * Convert the row data to markdown and update the element value
     * @param data - the updated row data
     */
    const handleUpdateRowData = useCallback(
      (data: Record<string, string>[]) => {
        // Convert newlines to <br /> tags in the data
        const processedData = data.map((row) => {
          const processedRow = { ...row };
          Object.keys(processedRow).forEach((key) => {
            if (typeof processedRow[key] === "string") {
              processedRow[key] = processedRow[key].replace(/\n/g, "<br />");
            }
          });
          return processedRow;
        });

        const markdown = json2md({
          table: {
            headers: element.options.columns.map((col) => col.name),
            rows: processedData,
          },
        });

        const hasValueChanged = markdown !== value;
        if (hasValueChanged) {
          onChange?.(element.id, { answer: markdown });
        }
      },
      [element.id, element.options.columns, onChange, value]
    );

    const handleAddRow = (e: React.MouseEvent) => {
      e.stopPropagation();

      // If no row is selected, insert a new row at the end
      const rowIndex = selectedRow
        ? rowData.findIndex((row) => row.id === selectedRow)
        : rowData.length - 1;

      // Insert a new empty object
      const updatedRowData = [
        ...rowData.slice(0, rowIndex + 1),
        // Add a new row with empty values
        {
          id: Date.now().toString(),
          ...element.options.columns.reduce(
            (obj, col) => {
              obj[col.name] = "";
              return obj;
            },
            {} as Record<string, string>
          ),
        },
        ...rowData.slice(rowIndex + 1),
      ];
      handleUpdateRowData(updatedRowData);
      handleClose();
    };

    const handleDeleteRow = (e: React.MouseEvent) => {
      e.stopPropagation();

      handleUpdateRowData(rowData.filter((row) => row.id !== selectedRow));
      handleClose();
    };

    const [rowSelectionModel, setRowSelectionModel] =
      useState<GridRowSelectionModel>([]);

    const handleRowOrderChange = async (params: GridRowOrderChangeParams) => {
      const rowsClone = [...rowData];
      const row = rowsClone.splice(params.oldIndex, 1)[0];
      rowsClone.splice(params.targetIndex, 0, row);

      handleUpdateRowData(rowsClone);
      // We need to reset the selection model and update the cell focus since the id changes and the selection model is based on the id.
      // The result otherwise would be that the old cell and row would stay selected since they now have the id of the dragged row and thus create confusion
      setRowSelectionModel([]);
      apiRef.current.setCellFocus(params.targetIndex.toString(), "__reorder__");
    };

    const getRowClassName = (params: GridRowClassNameParams) =>
      params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";

    const [cellModesModel, setCellModesModel] =
      React.useState<GridCellModesModel>({});

    const handleCellClick = (
      params: GridCellParams,
      event: React.MouseEvent
    ) => {
      if (!params.isEditable) {
        return;
      }

      // Ignore portal
      if (
        (event.target as any).nodeType === 1 &&
        !event.currentTarget.contains(event.target as Element)
      ) {
        return;
      }

      setCellModesModel((prevModel) => {
        return {
          // Revert the mode of the other cells from other rows
          ...Object.keys(prevModel).reduce(
            (acc, id) => ({
              ...acc,
              [id]: Object.keys(prevModel[id]).reduce(
                (acc2, field) => ({
                  ...acc2,
                  [field]: { mode: GridCellModes.View },
                }),
                {}
              ),
            }),
            {}
          ),
          [params.id]: {
            // Revert the mode of other cells in the same row
            ...Object.keys(prevModel[params.id] || {}).reduce(
              (acc, field) => ({
                ...acc,
                [field]: { mode: GridCellModes.View },
              }),
              {}
            ),
            [params.field]: { mode: GridCellModes.Edit },
          },
        };
      });
    };

    const handleCellModesModelChange = (newModel: GridCellModesModel) => {
      setCellModesModel(newModel);
    };

    const saveTimeoutRef = useRef<NodeJS.Timeout>();

    // Cleanup function to prevent memory leaks
    useEffect(() => {
      return () => {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
      };
    }, []);

    /**
     * Debounced function to save row data changes
     * - Clears any pending save operation
     * - Schedules a new save after 300ms
     * - Only saves if there are actual changes
     */
    const debouncedSave = useCallback(() => {
      // Clear any pending save operation
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Schedule a new save operation
      saveTimeoutRef.current = setTimeout(() => {
        // Get all cells currently being edited
        const editRows = apiRef.current.state.editRows;
        if (Object.keys(editRows).length === 0) return;

        // Create a copy of the current row data
        const updatedRowData = [...rowData];

        // Update each edited row
        Object.entries(editRows).forEach(([rowId, fields]) => {
          const rowIndex = updatedRowData.findIndex((row) => row.id === rowId);
          if (rowIndex !== -1) {
            // Update each edited field in the row
            Object.entries(fields).forEach(([field, value]) => {
              updatedRowData[rowIndex] = {
                ...updatedRowData[rowIndex],
                [field]: value.value,
              };
            });
          }
        });

        // Save the updated data
        handleUpdateRowData(updatedRowData);
      }, 300);
    }, [apiRef, rowData, handleUpdateRowData]);

    /**
     * Handles keyboard events in table cells
     * - Enter key: Saves current edit and moves to next cell
     * - Other keys: Triggers debounced save
     */
    const handleCellKeyDown = useCallback(
      ({ id, field }: GridCellParams, event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
          event.preventDefault();
          event.stopPropagation();

          // Handle Enter key in edit mode
          if (cellModesModel[id]?.[field]?.mode === GridCellModes.Edit) {
            // Get the edited value from the grid state
            const newInput = apiRef.current.state.editRows[id]?.[field]?.value;
            const row = rowData.find((row) => row.id === id);
            const rowIndex = rowData.findIndex((row) => row.id === id);

            // Update the row with the new value
            if (row) {
              const updatedRowData = [
                ...rowData.slice(0, rowIndex),
                {
                  ...row,
                  [field]: newInput || "",
                },
                ...rowData.slice(rowIndex + 1),
              ];
              handleUpdateRowData(updatedRowData);
            }

            // Move focus to the same column in the next row
            const nextRowIndex = rowIndex + 1;
            if (nextRowIndex < rowData.length) {
              const nextRow = rowData[nextRowIndex];
              apiRef.current.setCellFocus(nextRow.id, field);
            }

            // Exit edit mode for the current cell
            setCellModesModel((prev) => ({
              ...prev,
              [id]: {
                ...prev[id],
                [field]: { mode: GridCellModes.View },
              },
            }));
          } else {
            // Start edit mode if not already editing
            setCellModesModel((prev) => ({
              ...prev,
              [id]: {
                ...prev[id],
                [field]: { mode: GridCellModes.Edit },
              },
            }));
          }
          return;
        }
        // For other keys, trigger debounced save
        debouncedSave();
      },
      [apiRef, cellModesModel, rowData, handleUpdateRowData, debouncedSave]
    );

    return (
      <div className="flex flex-col">
        <DataGridPremium
          apiRef={apiRef}
          rows={rowData}
          autoHeight
          columns={columns}
          ignoreValueFormatterDuringExport
          getRowHeight={() => "auto"}
          autosizeOnMount
          cellSelection
          rowReordering
          cellModesModel={cellModesModel}
          onCellModesModelChange={handleCellModesModelChange}
          onCellClick={handleCellClick}
          onCellKeyDown={handleCellKeyDown}
          disableColumnMenu
          slots={{ ...slots, loadingOverlay: LoadingOverlay }}
          loading={isFetchingSuggestion}
          scrollbarSize={0}
          slotProps={{
            row: {
              onContextMenu: handleContextMenu,
            },
          }}
          sx={{
            ...tableStyles(isFetchingSuggestion),
            "& .reorder-column": {
              display: "flex",
              width: "100%",
              height: "100%",
            },
          }}
          hideFooter
          onRowOrderChange={handleRowOrderChange}
          getRowClassName={getRowClassName}
          autosizeOptions={autosizeOptions}
          disableRowSelectionOnClick
          onRowSelectionModelChange={(
            newRowSelectionModel: GridRowSelectionModel
          ): void => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          onCellEditStart={(params, event) => {
            if (params.reason === GridCellEditStartReasons.printableKeyDown) {
              event.defaultMuiPrevented = true;
            }
          }}
        />
        {!readOnly && element.options.addRowEnabled !== false && (
          <Button
            variant="text"
            color="inherit"
            disabled={isFetchingSuggestion}
            onClick={handleAddRow}
            className="py-4"
          >
            Add row
          </Button>
        )}
        {!readOnly && (
          <Menu
            open={contextMenu !== null}
            onClose={handleClose}
            anchorReference="anchorPosition"
            anchorPosition={
              contextMenu !== null
                ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                : undefined
            }
            slotProps={{
              root: {
                onContextMenu: (e) => {
                  e.preventDefault();
                  handleClose();
                },
              },
            }}
          >
            <MenuItem onClick={handleDeleteRow}>Delete row</MenuItem>
            <MenuItem onClick={handleAddRow}>Insert row below</MenuItem>
          </Menu>
        )}
      </div>
    );
  }
);
